import React, { useMemo, useContext, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    LineElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Tooltip,
    Filler,
} from "chart.js";
import { Box } from "@mui/material";
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { calculateYAxisConfig } from '../../helpers/chartHelpers';
import { ToolContext } from "../../../../../core/context/ToolContext";
import { getDateRangeByFilter, shouldFetchForFilter } from "../../helpers/dateRangeHelper";
import { SnakeLoader } from "../../../../../core/loader/SnakeLoader";
import ChartFilter from "../../helpers/chartFilter";
import { fetchFacebookInsights, fetchInstagramInsights, processInsightsData } from "../../helpers/metricHelpers";
import { generateDateRangeIntervals } from "../../helpers/dateRangeInterval";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Filler);

const createGradient = (ctx, colorStart, colorEnd) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
    gradient.addColorStop(0, colorStart);
    gradient.addColorStop(1, colorEnd);
    return gradient;
};

const verticalLinePlugin = {
    id: "verticalLineOnHover",
    afterDraw: (chart) => {
        if (chart.tooltip?._active && chart.tooltip._active.length) {
            const ctx = chart.ctx;
            ctx.save();
            const activePoint = chart.tooltip._active[0];
            ctx.beginPath();
            ctx.moveTo(activePoint.element.x, chart.chartArea.top);
            ctx.lineTo(activePoint.element.x, chart.chartArea.bottom);
            ctx.lineWidth = 2;
            ctx.strokeStyle = "#001DFF";
            ctx.stroke();
            ctx.restore();
        }
    }
};

export const ReachChart = () => {
    const { social_media_insight_state } = useContext(ToolContext)
    const platform = social_media_insight_state.connectedPlatform.data;

    const listView = async () => {
        if (platform == '' || social_media_insight_state.showProfilesModal.data) return;
        try {
            social_media_insight_state.reachLoading.set(true)
            const dateRange = getDateRangeByFilter(
                social_media_insight_state.reachFilter.data,
                social_media_insight_state.reachCustomRange.data
            );

            let insightsData = [];
            if (platform == 'facebook') {
                const chartData = await fetchFacebookInsights({
                    profileId: social_media_insight_state.selectedProfileId.data,
                    accessToken: social_media_insight_state.accessToken.data,
                    startDate: dateRange.start_date_unix,
                    endDate: dateRange.end_date_unix,
                });
                insightsData = chartData;
            } else if (platform == 'instagram') {
                const chartData = await fetchInstagramInsights({
                    accountId: social_media_insight_state.instagramAccountId.data,
                    accessToken: social_media_insight_state.instagramAccessToken.data,
                    start_date: dateRange.start_date_unix,
                    end_date: dateRange.end_date_unix,
                });
                insightsData.data = chartData;
            }

            const reachByDate = processInsightsData({
                insightsData,
                startDate: dateRange.start_date,
                endDate: dateRange.end_date,
                targetMetric: platform == 'facebook' ? 'page_impressions_unique' : 'reach'
            });
            const resReachData = generateDateRangeIntervals(
                dateRange.start_date,
                dateRange.end_date,
                reachByDate,
                platform == 'facebook' ? 'page_impressions_unique' : 'reach'
            );
            social_media_insight_state.reachData.set(resReachData)
        } catch (error) {
            console.error('Error fetching social media insights:', error);
            social_media_insight_state.reachData.set([]);
        } finally {
            social_media_insight_state.reachLoading.set(false)
        }
    }

    useEffect(() => {
        if (shouldFetchForFilter(social_media_insight_state.reachFilter.data, social_media_insight_state.reachCustomRange.data)) {
            listView();
        }
    }, [social_media_insight_state.reachFilter.data, social_media_insight_state.reachCustomRange.data, platform, social_media_insight_state.urlCode.data])

    const reachData = social_media_insight_state?.reachData?.data || [];
    const labels = reachData?.map(item => item.date) || [];
    const values = reachData?.map(item => item.value) || [];
    const yAxisConfig = calculateYAxisConfig(values);
    const data = useMemo(() => ({
        labels: labels,
        datasets: [
            {
                label: "Reach",
                data: values,
                fill: true,
                backgroundColor: (context) => {
                    const ctx = context.chart.ctx;
                    return createGradient(ctx, '#ADB7F9FF', '#B1B9F800');
                },
                borderColor: "#001DFF",
                pointBackgroundColor: "#001DFF",
                pointBorderColor: "#fff",
                pointRadius: 0,
                tension: 0.4,
                borderWidth: 2,
            },
        ],
    }), [labels]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                grid: {
                    display: false,
                    drawBorder: false,
                },
                ticks: {
                    color: '#666',
                    font: {
                        size: 12,
                    },
                },
                border: {
                    display: false,
                }
            },
            y: {
                ...yAxisConfig,
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.07)',
                    drawBorder: false,
                    lineWidth: 1,
                    borderDash: [5, 5],
                },
                border: {
                    display: false,
                }
            },
        },
        plugins: {
            tooltip: {
                backgroundColor: '#061D22',
                padding: 12,
                cornerRadius: 18,
                displayColors: false,
                titleFont: {
                    size: 0
                },
                bodyFont: {
                    size: 14,
                    weight: '600'
                },
                callbacks: {
                    title: () => '',
                    label: (context) => {
                        const label = context.dataset.label;
                        const value = context.parsed.y;
                        return `${value} ${label.toLowerCase()}`;
                    },
                },
                position: 'nearest',
                intersect: false,
                mode: 'index',
            },
            legend: {
                display: false,
            },
        },
        interaction: {
            intersect: false,
            mode: 'index',
        },
        layout: {
            padding: {
                left: 0,
                right: 0
            }
        }
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box fontSize={18} fontWeight={700} mb={2}>
                    Reach
                </Box>
                <Box display="flex" gap={2}>
                    <ChartFilter
                        filterValue={social_media_insight_state.reachFilter.data}
                        setFilterValue={social_media_insight_state.reachFilter.set}
                        customRange={social_media_insight_state.reachCustomRange.data}
                        setCustomRange={social_media_insight_state.reachCustomRange.set}
                        // minDate={dayjs().subtract(2, 'year').toDate()}
                        maxDate={dayjs().toDate()}
                    />
                </Box>
            </Box>
            <div style={{ height: '300px', width: '100%' }}>
                {social_media_insight_state.reachLoading.data ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                        <SnakeLoader size="1rem" bg="#11783C" distance="0.3rem" />
                    </ Box>
                ) :
                    (reachData.length > 0 && reachData.find(m => m.value > 0)) ? (
                        <Line data={data} options={options} plugins={[verticalLinePlugin]} />
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                            No data available
                        </Box>
                    )}
            </div>
        </Box>
    );
};